import { baseApiAxios } from './js/portal/api';
import TileDonutChart from './jsx/components/tiles/TileDonutChart';
import TileText from './jsx/components/tiles/TileText';
import ChartHorizontalStacked from './jsx/components/tiles/ChartHorizontalStacked';
import ChartLegend from './jsx/components/tiles/ChartLegend';
import {
  defaultColors,
  loadDonutChart,
  loadStackedAbsoluteValuesChart,
  loadTextTile,
  loadValueInRangeTile,
  parseInitialTimeframeOptions,
  createTimeframeController,
  StatisticsPageHeader,
  withStatsLoading,
  tileTitles,
} from './js/portal/statistics';
import TileValueInRange from './jsx/components/tiles/TileValueInRange';
import TileTable from './jsx/components/tiles/TileTable';
import { initDataTable, exportButtons, col } from './js/portal/datatable';
import { addCopyButton } from './js/portal/main';

export default async function StatisticsOrderPage() {
  let orderActionsData = {},
    orderData = {};

  const controller = createTimeframeController({
    loadInitialData: async (radioSelection, dropdownSelection) => {
      await loadOrderActionsData(radioSelection, dropdownSelection);
      return parseInitialTimeframeOptions(orderActionsData?.stats, radioSelection);
    },
    loadEmptyTiles,
    loadDashboardData,
    getStatsData: () => orderActionsData,
  });

  const page = (
    <div id="dashboard_overview_account">
      <StatisticsPageHeader
        title="Order Statistics"
        dropdownChangeListener={controller.dropdownChangeListener}
        radioChangeListener={controller.radioChangeListener}
      />
      <div class="row">
        <TileValueInRange
          id="order-success-rate"
          title="Order Success Rate"
          range="gray"
          rangeLabel="no data"></TileValueInRange>
        <TileText id="successful-orders" title="Successful Orders"></TileText>
        <TileText id="failed-orders" title="Failed Orders"></TileText>
        <TileText id="top-0-order" title="Top Order" highlightColor="var(--bs-secondary)"></TileText>
        <TileText id="top-1-order" title="Top Order" highlightColor="var(--bs-secondary)"></TileText>
        <TileText id="top-2-order" title="Top Order" highlightColor="var(--bs-secondary)"></TileText>
        <TileText id="failed-0-order" title="Failed Order" highlightColor="var(--bs-danger)"></TileText>
        <TileText id="failed-1-order" title="Failed Order" highlightColor="var(--bs-danger)"></TileText>
        <TileText id="failed-2-order" title="Failed Order" highlightColor="var(--bs-danger)"></TileText>
        <TileDonutChart
          id="orders-by-type"
          title="Orders by Type"
          labels={[]}
          colors={defaultColors}
          partial={26 / 30}></TileDonutChart>
        <TileText id="orders-by-type-and-state" title="Number of Orders" headline="by Type and State">
          <ChartHorizontalStacked
            title=""
            hideLegend={true}
            values={[]}
            colors={defaultColors}></ChartHorizontalStacked>
          <ChartLegend labels={[]} colors={defaultColors}></ChartLegend>
        </TileText>
        <TileDonutChart
          id="orders-by-state"
          title="Orders by State"
          labels={[]}
          colors={defaultColors}
          partial={26 / 30}></TileDonutChart>
        <TileTable id="order-data"></TileTable>
      </div>
    </div>
  );

  async function onPageReady() {
    controller.onPageReady();
  }

  // LOAD AND DISPLAY DATA

  function loadEmptyTiles() {
    loadValueInRangeTile('order-success-rate', 0.0, 'gray', 'no data');
    loadTextTile('successful-orders', '-', null, true);
    loadTextTile('failed-orders', '-', null, true);
    [...Array(3)]
      .map(_ => ['Top Order', '-'])
      .forEach((item, index) => loadTextTile(`top-${index}-order`, item[1], null, true, item[0]));
    [...Array(3)]
      .map(_ => ['Failed Order', '-'])
      .forEach((item, index) => loadTextTile(`failed-${index}-order`, item[1], null, true, item[0], null));
    loadStackedAbsoluteValuesChart(
      'orders-by-type-and-state',
      'Number of Orders',
      'by Type and State',
      {},
      [],
      10,
      true,
      true,
    );
    loadDonutChart('orders-by-state', 'Orders by State', {});
    loadDonutChart('orders-by-type', 'Orders by Type', {});
    $('#table-order-data').DataTable().clear().draw();
  }

  function loadDashboardData(timeframe, sk) {
    loadOrderActionsData(timeframe, sk);
    loadOrderData(timeframe, sk);
  }

  async function loadOrderActionsData(timeframe, sk) {
    await withStatsLoading(async () => {
      orderActionsData = await baseApiAxios.get(
        `/stats/categories/orders/statistics/actions?time_frame=${timeframe}${sk ? '&sort_key=' + sk : ''}`,
        null,
        {},
      );

      if (orderActionsData?.stats?.length) {
        let chartData = orderActionsData.stats[0].data;

        let range, rangeLabel;
        if (chartData.success_rate > 98.0) {
          range = 'green';
          rangeLabel = 'excellent';
        } else if (96.0 < chartData.success_rate && chartData.success_rate <= 98.0) {
          range = 'yellow';
          rangeLabel = 'good';
        } else if (chartData.success_rate <= 96.0) {
          range = 'red';
          rangeLabel = 'warning';
        }

        loadValueInRangeTile('order-success-rate', chartData.success_rate, range, rangeLabel);
        loadTextTile('successful-orders', chartData.success, null, true);
        loadTextTile('failed-orders', chartData.failed, null, true);
      }
    });
  }

  async function _fetchOrderData(timeframe, sk) {
    orderData = await baseApiAxios.get(
      `/stats/categories/orders?time_frame=${timeframe}${sk ? '&sort_key=' + sk : ''}`,
      null,
      {},
    );

    let stateLabels = ['new', 'in_progress', 'failed', 'success', 'not_completed'];
    if (orderData?.stats?.length) {
      let chartData = {},
        typeData = {},
        stateData = {},
        failedData = {};

      orderData.stats.forEach(dataset => {
        if (dataset.stat === 'actions' || dataset.sk !== sk) return;
        typeData[dataset.stat] = dataset.data.count;
        failedData[dataset.stat] = dataset.data.failed;
        let dataObj = {};
        stateLabels.forEach(label => {
          stateData[label] = (stateData[label] || 0) + dataset.data[label];
          dataObj[label] = { count: dataset.data[label] };
        });
        chartData[dataset.stat] = Object.assign(
          {
            count: Object.values(dataObj)
              .map(a => a.count)
              .reduce((a, b) => a + b),
          },
          dataObj,
        );
      });

      loadStackedAbsoluteValuesChart(
        'orders-by-type-and-state',
        'Number of Orders',
        'by Type and State',
        chartData,
        stateLabels,
        Math.max(...Object.values(typeData)),
        true,
        true,
      );
      loadDonutChart('orders-by-state', 'Orders by State', stateData);
      loadDonutChart('orders-by-type', 'Orders by Type', typeData);
      const mostRequested = Object.entries(typeData)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3);
      const mostFailed = Object.entries(failedData)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3);
      mostRequested.forEach((item, index) =>
        loadTextTile(`top-${index}-order`, item[1], null, true, tileTitles[item[0]], 'var(--bs-secondary)'),
      );
      mostFailed.forEach((item, index) =>
        loadTextTile(
          `failed-${index}-order`,
          item[1],
          null,
          true,
          tileTitles[item[0]],
          'var(--bs-danger)',
          'Failed Orders',
        ),
      );
      loadOrderTable();
    }
  }

  async function loadOrderData(timeframe, sk) {
    await withStatsLoading(() => _fetchOrderData(timeframe, sk));
  }

  async function loadOrderTable() {
    await withStatsLoading(async () => {
      const tableData = orderData?.stats.map(dataset => Object.assign({ type: dataset.stat }, dataset.data));

      initDataTable(
        'table-order-data',
        exportButtons({ copy: false }),
        [
          col('type', 'type_col', 'Order Type'),
          col('count', 'count_col', 'Count'),
          col('failed', 'failed_col', 'Failed'),
          col('in_progress', 'in_progress_col', 'In Progress'),
          col('not_completed', 'not_completed_col', 'Not Completed'),
          col('new', 'new_col', 'New'),
          col('success', 'success_col', 'Successful'),
          col('success_rate', 'success_rate_col', 'Success Rate', {
            createdCell: function (td, cellData) {
              addCopyButton(td);
              if (cellData > 98.0) $(td).addClass('portal-success-background');
              else if (96.0 < cellData && cellData <= 98.0) $(td).addClass('portal-warning-background');
              else if (cellData <= 96.0) $(td).addClass('portal-danger-background');
            },
            render: function (cellData) {
              return `${cellData.toFixed(2)}%`;
            },
          }),
        ],
        null,
        null,
      );

      const dt = $('#table-order-data').DataTable();
      dt.rows().remove();
      dt.rows.add(tableData).draw(false);
    });
  }

  return [page, onPageReady];
}
